import { Col, Form, Row, Table } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import AkcionarCard from 'components/molecules/Akcionar/AkcionarCard';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import { IPrehled } from 'services/akcionar/prehled';
import {
    IHromadneHlasovaniUcty,
    IHromadneHlasovaniUctyInput,
    hromadneHlasovaniUcetVybrat,
    hromadneHlasovaniUctyGET,
    hromadneHlasovaniUctyVSE,
    hromadneHlasovaniZalozeni
} from 'services/spravceUctu/hromadneHlasovani';
import { IHlasovaniTableData, IUctyUdalosti, hlasovaniTableDataApi } from 'services/spravceUctu/spravceHlasovani';
import prehledApi from 'services/spravceUctu/spraveUctuPrehled';

import { EStavHl } from 'enums/akcionarKorpAkce/stavHl';
import routePathEnum from 'enums/routePathEnum';

interface IPrehledSpravce {}

const PrehledSpravce: React.FC<IPrehledSpravce> = () => {
    const [form] = Form.useForm();
    const { setLevel } = useProfily();
    const { navigateWorkaround } = useNavigateWorkaround();
    const { spravceSelectedCard, aktivniFirma } = useContext(Context);
    const baseURL = `${routePathEnum.FIRMA_PLAIN}${aktivniFirma?.iducet}`;
    const [prehled, setPrehled] = useState<IPrehled[]>([]);
    const [errMessage, setErrMessage] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const [refetch, setRefetch] = useState<boolean>(false);
    const [loadingTable, setLoadingTable] = useState<boolean>(false);
    const [tableOffset, setTableOffset] = useState<number>(0);
    const [pagination, setPagination] = useState<IHromadneHlasovaniUctyInput>({
        order: 'oid:desc',
        limit: 10,
        offset: 0
    });
    const paginationSelected = {
        order: 'oid:desc',
        limit: 999,
        offset: 0
    };
    const [tableData, setTableData] = useState<IHlasovaniTableData>();
    const [tableDataHromadni, setTableDataHromadni] = useState<IHromadneHlasovaniUcty>();
    const [tableDataHromadniVybrane, setTableDataHromadniVybrane] = useState<IHromadneHlasovaniUcty>();
    const [selectedUcty, setSelectedUcty] = useState<IHromadneHlasovaniUcty['uctyudalosti']>([]);
    const isUcastnik = window.location.href.includes('ucastnik');

    useEffect(() => {
        if (!isUcastnik) {
            setLoading(true);
            setErrMessage('');
            prehledApi()
                .then(resp => setPrehled(resp))
                .catch((err: any) => setErrMessage(err.response?.data.message))
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (spravceSelectedCard) {
            setLoadingTable(true);
            setErrMessage('');
            if (spravceSelectedCard.hromadneHlasovaniID) {
                hromadneHlasovaniUctyGET(spravceSelectedCard.hromadneHlasovaniID, pagination)
                    .then(data => setTableDataHromadni(data))
                    .finally(() => setLoadingTable(false));
                hromadneHlasovaniUctyGET(spravceSelectedCard.hromadneHlasovaniID, {
                    ...paginationSelected,
                    vybran: true
                })
                    .then(data => {
                        setTableDataHromadniVybrane(data);
                        setSelectedUcty(data.uctyudalosti);
                    })
                    .finally(() => setLoadingTable(false));
            } else {
                hlasovaniTableDataApi(spravceSelectedCard.id, {
                    ...pagination,
                    offset: tableOffset
                })
                    .then(data => setTableData(data.data))
                    .finally(() => setLoadingTable(false));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spravceSelectedCard, tableOffset, refetch, pagination]);

    const columns = () => [
        {
            title: <TranslateText>AKCIONAR_PROFIL_MAJETKOVY_UCET</TranslateText>,
            dataIndex: 'ucet',
            key: 'ucet'
        },
        {
            title: <TranslateText>MAJITEL</TranslateText>,
            dataIndex: 'nazev',
            key: 'nazev'
        },
        {
            title: <TranslateText>SRD_PRUVODCE_VH_E_KVORUM_POCET</TranslateText>,
            dataIndex: 'pocet',
            key: 'pocet'
        },

        {
            title: <TranslateText>STAV</TranslateText>,
            dataIndex: 'stavhl',
            key: 'stavhl',
            render: (text: EStavHl) => (
                <TranslateText>{text ? `SRD_PRUVODCE_VH_E_PRIDAT_VOTETP_${EStavHl[text]}` : ''}</TranslateText>
            )
        },
        ...(spravceSelectedCard?.hromadneHlasovaniID
            ? []
            : [
                  {
                      dataIndex: 'oid',
                      key: 'oid',
                      render: (_: string, row: IUctyUdalosti, index: number) => (
                          <AnchorComponent
                              onClick={() => {
                                  navigateWorkaround(
                                      // eslint-disable-next-line max-len
                                      `../firma/${aktivniFirma?.iducet}/hlasovani-spravce/${row.oid}/${spravceSelectedCard?.id}`
                                  );
                              }}
                              id={`spraveHlasovat${index}Btn`}
                          >
                              <TranslateText>
                                  {dayjs(spravceSelectedCard?.eltermin) >= dayjs() ? 'HLASOVAT' : 'ZOBRAZIT'}
                              </TranslateText>
                          </AnchorComponent>
                      ),
                      align: 'center' as 'center'
                  }
              ])
    ];

    return (
        <>
            <Title level={3} text={!spravceSelectedCard ? 'PREHLED_UDALOSTI' : 'UDALOST'} />
            {!spravceSelectedCard && (
                <>
                    <Row gutter={8}>
                        <Col
                            span={24}
                            style={{
                                alignContent: 'center'
                            }}
                        >
                            <LoadingWrapper loading={loading}>
                                {errMessage ? (
                                    <Title text="AKCIONAR_ZADNE_UDALOSTI" level={3} />
                                ) : (
                                    <AkcionarCard prehled={prehled} isSpravce={true} />
                                )}
                            </LoadingWrapper>
                        </Col>
                    </Row>
                </>
            )}
            {spravceSelectedCard && (
                <>
                    <AkcionarCard prehled={[spravceSelectedCard]} isSpravce={true} noButtons={true} />
                    <FormWrapper
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onFinish={(values: any) =>
                            setPagination(pv => {
                                return Object.fromEntries(
                                    Object.entries({
                                        ...pv,
                                        ...values,
                                        offset: 0
                                    }).filter(([, v]) => v !== undefined && v !== '')
                                ) as unknown as IHromadneHlasovaniUctyInput;
                            })
                        }
                        outForm={form}
                    >
                        <Row
                            gutter={8}
                            style={{
                                marginTop: 24
                            }}
                        >
                            <Col span={6}>
                                <InputComponent
                                    label="AKCIONAR_PROFIL_MAJETKOVY_UCET"
                                    name="ucet"
                                    notRequired={true}
                                    disableEdit={true}
                                />
                            </Col>
                            <Col span={6}>
                                <InputComponent label="MAJITEL" name="majitel" notRequired={true} disableEdit={true} />
                            </Col>
                            <Col span={6}>
                                <SelectComponent
                                    disableEdit={true}
                                    name="stav"
                                    labelParagraph="STAV"
                                    notRequired={true}
                                    isFormItem={true}
                                    data={[
                                        {
                                            labelcz: 'SRD_HLASOVANI_STAV_C',
                                            value: 'HLASOVANO'
                                        },
                                        ...(dayjs(spravceSelectedCard.eltermin) < dayjs()
                                            ? [
                                                  {
                                                      labelcz: 'SRD_HLASOVANI_STAV_D',
                                                      value: 'LHUTA'
                                                  }
                                              ]
                                            : [
                                                  {
                                                      labelcz: 'SRD_HLASOVANI_STAV_A',
                                                      value: 'NEHLASOVANO'
                                                  },
                                                  {
                                                      labelcz: 'SRD_HLASOVANI_STAV_B',
                                                      value: 'ROZP'
                                                  }
                                              ])
                                    ]}
                                    allowClear={true}
                                />
                            </Col>
                        </Row>
                        <Row
                            className="button-row-modal"
                            style={{
                                position: 'relative',
                                top: -16
                            }}
                        >
                            <ButtonComponent buttontext={'FILTROVAT'} htmlType="submit" id="filtrBtn" />
                            <ButtonComponent
                                buttontext={'VSECHNY'}
                                onClick={() => {
                                    setTimeout(() => form.resetFields(), 100);
                                    setPagination({
                                        order: 'oid:desc',
                                        limit: 10,
                                        offset: 0
                                    });
                                }}
                                id="resetFiltrBtn"
                                htmlType="button"
                            />
                        </Row>
                    </FormWrapper>
                    {spravceSelectedCard?.hromadneHlasovaniID ? (
                        <>
                            <Paragraph
                                text="SRD_HROMADNE_TEXT"
                                style={{
                                    fontWeight: 'bold'
                                }}
                            />
                            <Table
                                rowKey={e => e.oid}
                                columns={columns()}
                                dataSource={tableDataHromadni?.uctyudalosti}
                                loading={loadingTable}
                                pagination={{
                                    total: tableDataHromadni?.count,
                                    onChange: page =>
                                        setPagination(pv => {
                                            return {
                                                ...pv,
                                                offset: (page - 1) * 10
                                            };
                                        }),
                                    current: (pagination.offset || 0) / 10 + 1,
                                    showSizeChanger: false
                                }}
                                rowSelection={{
                                    onSelectAll: selected => {
                                        setLoading(true);
                                        hromadneHlasovaniUctyVSE(
                                            spravceSelectedCard.hromadneHlasovaniID!,
                                            selected,
                                            pagination
                                        )
                                            .then(() => setRefetch(pv => !pv))
                                            .finally(() => setLoading(false));
                                    },
                                    onSelect: (record, selected) => {
                                        //       setLoading(true);
                                        selected
                                            ? setSelectedUcty(pv => {
                                                  return [...pv, record];
                                              })
                                            : setSelectedUcty(pv => pv.filter(e => e.oid !== record.oid));
                                        hromadneHlasovaniUcetVybrat(
                                            spravceSelectedCard.hromadneHlasovaniID!,
                                            record.oid,
                                            selected
                                        ).then(() => setRefetch(pv => !pv));
                                        //    .finally(() => setLoading(false));
                                    },
                                    selectedRowKeys: selectedUcty.map(e => e.oid) /* tableDataHromadni?.uctyudalosti
                                        ?.filter(e => e.vybran)
                                        .map(e => e.oid)*/
                                }}
                            />
                            {!!selectedUcty.length && (
                                <>
                                    <Title level={5} text="Vybrané účty" />
                                    <Table
                                        rowKey={e => e.oid}
                                        columns={columns()}
                                        dataSource={selectedUcty.sort((a, b) => b.oid - a.oid)}
                                        pagination={{
                                            total: selectedUcty?.length,
                                            showSizeChanger: false
                                        }}
                                        rowSelection={{
                                            /*  onSelectAll: selected => {
                                                const filterVals = {
                                                    ...pagination,
                                                    vybran: selected
                                                };
                                                delete filterVals.limit;
                                                delete filterVals.offset;
                                                delete filterVals.order;
                                                setLoading(true);
                                                hromadneHlasovaniUctyVSE(spravceSelectedCard.hromadneHlasovaniID!, {
                                                    ...filterVals,
                                                    vse: false
                                                })
                                                    .then(() => setRefetch(pv => !pv))
                                                    .finally(() => setLoading(false));
                                            },*/
                                            hideSelectAll: true,
                                            onSelect: (record, selected) => {
                                                // setLoading(true);
                                                setSelectedUcty(pv => pv.filter(e => e.oid !== record.oid));
                                                hromadneHlasovaniUcetVybrat(
                                                    spravceSelectedCard.hromadneHlasovaniID!,
                                                    record.oid,
                                                    selected
                                                );
                                                // .then(() => setRefetch(pv => !pv))
                                                //  .finally(() => setLoading(false));
                                            },
                                            selectedRowKeys: selectedUcty?.map(e => e.oid)
                                        }}
                                    />
                                </>
                            )}
                            <Row className="button-row">
                                <ButtonComponent
                                    id="spravceZpetBtn"
                                    buttontext={'ZPET'}
                                    onClick={() => {
                                        setSelectedUcty([]);
                                        const spravceCardEdit = {
                                            ...spravceSelectedCard
                                        };
                                        delete spravceCardEdit.hromadneHlasovaniID;
                                        setLevel('spravceSelectedCard', {
                                            ...spravceCardEdit
                                        });
                                    }}
                                    type="default"
                                />
                                <ButtonComponent
                                    id="hromadnehlasovatBtn"
                                    buttontext={'POKRACOVAT'}
                                    disabled={!tableDataHromadniVybrane?.uctyudalosti.length}
                                    onClick={() =>
                                        navigateWorkaround(
                                            // eslint-disable-next-line max-len
                                            `../firma/${aktivniFirma?.iducet}/hlasovani-spravce/hromadne/${spravceSelectedCard?.hromadneHlasovaniID}`
                                        )
                                    }
                                />
                            </Row>
                        </>
                    ) : (
                        <>
                            <TableComponent
                                rowKey={e => e.oid}
                                columns={columns()}
                                dataSource={tableData?.uctyudalosti}
                                loading={loadingTable}
                                pagination={{
                                    total: tableData?.count,
                                    onChange: page => setTableOffset((page - 1) * 10),
                                    current: tableOffset / 10 + 1,
                                    showSizeChanger: false
                                }}
                            />
                            <Row className="button-row">
                                <ButtonComponent
                                    id="spravceZpetBtn"
                                    buttontext={'ZPET'}
                                    onClick={() => {
                                        if (isUcastnik) {
                                            navigateWorkaround(
                                                window.location.hash.includes('prehled-ucastnik-hlasovani')
                                                    ? `${baseURL}${routePathEnum.PREHLED_UCASTNIK}`
                                                    : `${baseURL}${routePathEnum.PREHLED_UCASTNIK_HLASOVANI}`
                                            );
                                        } else {
                                            setLevel('spravceSelectedCard', null);
                                        }
                                    }}
                                    type="default"
                                />
                                {dayjs(spravceSelectedCard.eltermin) >= dayjs() && (
                                    <ButtonComponent
                                        id="hromadneHlasovatBtn"
                                        buttontext={'HROMADNE_HLASOVAT'}
                                        onClick={() => {
                                            setLoading(true);
                                            hromadneHlasovaniZalozeni(spravceSelectedCard.id)
                                                .then(resp =>
                                                    setLevel('spravceSelectedCard', {
                                                        ...spravceSelectedCard,
                                                        hromadneHlasovaniID: resp.idhrom
                                                    })
                                                )
                                                .finally(() => setLoading(false));

                                            //  setLevel('srdHlasovani', selectedUcty);
                                            //  navigateWorkaround(`hlasovani-spravce/hromadne/${spravceSelectedCard?.id}`);
                                        }}
                                    />
                                )}
                            </Row>
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default PrehledSpravce;
